import React, { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Modal, Row, Select, Table, Tooltip, Typography, message } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useLazyQuery, useMutation } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { HasResource } from 'components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';
import {
    LOADING_ARCHIVE_TASKS,
    LoadingArchiveTaskFilter,
    LoadingArchiveTaskItem,
    LoadingArchiveTasksResponse,
} from './graphql/queries/loadingArchiveQueries';
import {
    ARCHIVE_LOADING_ORDER_DETAIL,
    ArchiveLoadingOrderDetailResponse,
    CANCEL_ARCHIVE_LOADING_ORDER_DETAIL,
    CancelArchiveLoadingOrderDetailResponse,
} from './graphql/mutations/loadingArchiveMutations';
import LoadingOrderDetailDrawer from './components/LoadingOrderDetailDrawer';
import { ARCHIVE_PAGE_SIZE_OPTIONS, ARCHIVE_STATUS, renderArchiveStatus } from './components/loadingArchiveShared';
import {
    BUSINESS_TYPE_MAP,
    DEFAULT_PAGINATION,
    TASK_STATUS_OPTIONS,
    TASK_TYPE_MAP,
    parseIdInput,
    renderTagged,
    renderTaskStatus,
    renderText,
    toOptions,
} from '../task-management/components/taskManagementShared';

const { Title } = Typography;
const { RangePicker } = DatePicker;

type FilterValues = {
    taskId?: string;
    taskNumber?: string;
    taskStatus?: number;
    taskType?: number;
    taskDateRange?: [Dayjs | null, Dayjs | null] | null;
};

const LoadingArchivePage: React.FC = () => {
    const [form] = Form.useForm<FilterValues>();
    const [activeFilter, setActiveFilter] = useState<LoadingArchiveTaskFilter>({});
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [detailTask, setDetailTask] = useState<{ id: number; taskNumber: string | null } | null>(null);
    const [archivingId, setArchivingId] = useState<number | null>(null);
    const [cancelingId, setCancelingId] = useState<number | null>(null);
    const [runQuery, { data, loading }] = useLazyQuery<LoadingArchiveTasksResponse>(LOADING_ARCHIVE_TASKS, {
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });
    const [runArchive] = useMutation<ArchiveLoadingOrderDetailResponse>(ARCHIVE_LOADING_ORDER_DETAIL, {
        onError: (e) => message.error(e.message),
    });

    const [runCancelArchive] = useMutation<CancelArchiveLoadingOrderDetailResponse>(CANCEL_ARCHIVE_LOADING_ORDER_DETAIL, {
        onError: (e) => message.error(e.message),
    });

    useEffect(() => {
        runQuery({
            variables: {
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [activeFilter, pagination, runQuery]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const taskId = parseIdInput(values.taskId);
        if (taskId === null) {
            message.warning('任务ID 必须是纯数字');
            return;
        }
        const [from, to] = values.taskDateRange ?? [null, null];
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({
            taskId,
            taskNumber: values.taskNumber?.trim() || undefined,
            taskStatus: values.taskStatus,
            taskType: values.taskType,
            taskDateFrom: from ? from.format('YYYY-MM-DD') : undefined,
            taskDateTo: to ? to.format('YYYY-MM-DD') : undefined,
        });
    };

    const handleArchive = (record: LoadingArchiveTaskItem) => {
        if (record.id === null) return;
        const taskId = record.id;
        const confirmArchive = () => Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '确认归档',
            okButtonProps: { danger: true },
            content: `即将把任务 ${record.taskNumber ?? taskId} 的全部装车包裹明细在同一事务内搬运到归档表，并从在线表删除（任务本身保留）。任一步校验失败会整体回滚；归档成功后不支持自动还原，确认继续？`,
            onOk: async () => {
                setArchivingId(taskId);
                try {
                    const result = await runArchive({ variables: { taskId } });
                    const payload = result.data?.opsToolArchiveTmsLoadingOrderDetail;
                    if (!payload) return;
                    if (!payload.success) {
                        message.error(payload.message);
                        return;
                    }
                    message.success(`归档成功，共 ${payload.updatedCount ?? 0} 条明细`);
                    // 按当前条件重查，刷新「归档情况」
                    setPagination((prev) => ({ ...prev }));
                } finally {
                    setArchivingId(null);
                }
            },
        });
        if (!record.riskyArchive) {
            confirmArchive();
            return;
        }
        // 任务日期在 2~3 个月前：距今较近，先做一次风险提醒，确认后再进入归档确认
        Modal.confirm({
            title: '近期任务归档提醒',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '我已知晓，继续',
            okButtonProps: { danger: true },
            content: `任务 ${record.taskNumber ?? taskId} 的任务日期为 ${record.taskDate ?? '-'}，距今不足 3 个月，装车数据可能仍有查询或追溯需求。确认要归档吗？`,
            onOk: () => confirmArchive(),
        });
    };

    const doCancelArchive = async (taskId: number) => {
        setCancelingId(taskId);
        try {
            const result = await runCancelArchive({ variables: { taskId } });
            const payload = result.data?.opsToolCancelArchiveTmsLoadingOrderDetail;
            if (!payload) return;
            if (!payload.success) {
                message.error(payload.message);
                return;
            }
            message.success(`归档回退成功，共 ${payload.updatedCount ?? 0} 条明细`);
            // 按当前条件重查，刷新「归档情况」
            setPagination((prev) => ({ ...prev }));
        } finally {
            setCancelingId(null);
        }
    };

    // 归档回退：两次确认（风险确认 → 最终确认点名任务号），第一次提示回退后可能被定时任务再次归档
    const handleCancelArchive = (record: LoadingArchiveTaskItem) => {
        if (record.id === null) return;
        const taskId = record.id;
        const taskLabel = record.taskNumber ?? String(taskId);
        Modal.confirm({
            title: '风险操作确认',
            icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
            okText: '继续',
            okButtonProps: { danger: true },
            content: `即将把任务 ${taskLabel} 的全部装车包裹明细在同一事务内从归档表搬回在线表，并删除归档表中的明细。若该任务仍满足自动归档条件（已完成/已取消且超过自动归档天数），定时任务会再次归档它，确认继续？`,
            onOk: () => {
                Modal.confirm({
                    title: '最终确认',
                    icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
                    okText: '我已知晓，确认回退',
                    okButtonProps: { danger: true },
                    content: `再次确认：回退任务 ${taskLabel} 的归档数据，确定执行？`,
                    onOk: () => doCancelArchive(taskId),
                });
            },
        });
    };

    const columns: ColumnsType<LoadingArchiveTaskItem> = [
        { title: '任务ID', dataIndex: 'id', width: 90, render: renderText },
        { title: '任务号', dataIndex: 'taskNumber', width: 170, render: renderText },
        { title: '路线名称', dataIndex: 'routeName', width: 200, render: renderText },
        { title: '任务类型', dataIndex: 'taskType', width: 100, render: renderTagged(TASK_TYPE_MAP) },
        { title: '业务类型', dataIndex: 'businessType', width: 110, render: renderTagged(BUSINESS_TYPE_MAP) },
        { title: '任务日期', dataIndex: 'taskDate', width: 110, render: renderText },
        { title: '任务状态', dataIndex: 'taskStatus', width: 100, render: renderTaskStatus },
        { title: '归档情况', dataIndex: 'archiveStatus', width: 110, render: renderArchiveStatus },
        {
            title: '操作',
            key: 'action',
            width: 260,
            fixed: 'right',
            render: (_, record) => {
                const noData = record.archiveStatus === ARCHIVE_STATUS.NO_DATA;
                const inconsistent = record.archiveStatus === ARCHIVE_STATUS.INCONSISTENT;
                const archived = record.archiveStatus === ARCHIVE_STATUS.ARCHIVED;
                const archiveButton = (
                    <Button
                        type="link"
                        size="small"
                        danger={record.archivable && record.riskyArchive}
                        disabled={!record.archivable}
                        loading={archivingId === record.id}
                        onClick={() => handleArchive(record)}
                    >
                        归档
                    </Button>
                );
                return (
                    <>
                        <Button
                            type="link"
                            size="small"
                            disabled={record.id === null || noData || inconsistent}
                            onClick={() => record.id !== null && setDetailTask({ id: record.id, taskNumber: record.taskNumber })}
                        >
                            查看装车包牌
                        </Button>
                        <HasResource resourceCodes={[ResourceCode.menu.OPS_TOOL_TMS]}>
                            {record.archivable || !record.notArchivableReason ? (
                                archiveButton
                            ) : (
                                <Tooltip title={record.notArchivableReason}>
                                    <span>{archiveButton}</span>
                                </Tooltip>
                            )}
                            <Button
                                type="link"
                                size="small"
                                danger
                                disabled={!archived}
                                loading={cancelingId === record.id}
                                onClick={() => handleCancelArchive(record)}
                            >
                                归档回退
                            </Button>
                        </HasResource>
                    </>
                );
            },
        },
    ];

    const result = data?.opsToolTmsLoadingArchiveTasks;

    return (
        <div className="p-6 space-y-4">
            <Title level={4}>装车数据归档</Title>
            <Form form={form}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="taskId" label="任务ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="taskNumber" label="任务号">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="taskStatus" label="任务状态">
                            <Select allowClear placeholder="全部" options={TASK_STATUS_OPTIONS} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="taskType" label="任务类型">
                            <Select allowClear placeholder="全部" options={toOptions(TASK_TYPE_MAP)} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="taskDateRange" label="任务日期">
                            <RangePicker allowEmpty={[true, true]} className="w-full" />
                        </Form.Item>
                    </Col>
                    <Col span={12} className="text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<LoadingArchiveTaskItem>
                rowKey={(record) => String(record.id)}
                size="small"
                loading={loading}
                columns={columns}
                dataSource={result?.content ?? []}
                pagination={false}
                scroll={{ x: 1200 }}
                className="mb-4"
            />
            <TablePaginationBar
                total={result?.totalElements ?? 0}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={ARCHIVE_PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
            <LoadingOrderDetailDrawer task={detailTask} onClose={() => setDetailTask(null)} />
        </div>
    );
};

export default LoadingArchivePage;
